/** @file Number field: drag the label to scrub (shift = fine), or type; min, max, step and precision props. */
import './tokens.css';
import './NumberField.css';
import { TextInput } from './TextInput';
import { constrainNumber, type NumberBounds } from './logic/constrainNumber';
import { formatNumber } from './logic/formatNumber';
import { joinClass } from './logic/joinClass';
import { parseNumberInput } from './logic/parseNumberInput';
import { stepPrecision } from './logic/stepPrecision';
import { useScrub } from './useScrub';

/** Props of NumberField; `min`, `max` and `precision` come from NumberBounds. */
export interface NumberFieldProps extends NumberBounds {
  value: number;
  /** Already-translated label: shown as the scrub handle and used as the input's accessible name. */
  label: string;
  /** Called once per finished edit (typed Enter/blur, or scrub release) with a rounded, clamped value that differs from `value`. */
  onCommit: (value: number) => void;
  /** Called on every scrub move so a viewport can preview; called again with `value` if the scrub is cancelled. */
  onPreview?: (value: number) => void;
  /** Value change per pixel of scrub; default 1. Shift scrubs at a tenth of it. Without `precision`, values keep the decimals of that fine step. */
  step?: number;
  disabled?: boolean;
  /** Already-translated unit shown after the input (for example a degree sign). */
  unit?: string;
}

/** The field's bounds; without a `precision` prop it is derived from the step, so a fine scrub never commits float noise such as 10.299999999999999. */
function boundsOf(props: NumberFieldProps, step: number): NumberBounds {
  const bounds: NumberBounds = { precision: props.precision ?? stepPrecision(step) };
  if (props.min !== undefined) bounds.min = props.min;
  if (props.max !== undefined) bounds.max = props.max;
  return bounds;
}

/** Labelled numeric input with drag-scrub on the label; a plain click on the label focuses the input. */
export function NumberField(props: NumberFieldProps) {
  const { value, label, onCommit, onPreview, step = 1, disabled = false, unit } = props;
  const bounds = boundsOf(props, step);
  const scrub = useScrub({ value, step, bounds, disabled, onPreview, onCommit });
  const commitText = (text: string): void => {
    const typed = parseNumberInput(text);
    const next = typed === null ? value : constrainNumber(typed, bounds);
    if (next !== value) onCommit(next);
  };
  return (
    <label className={joinClass('p2-number', disabled && 'p2-number-disabled')}>
      <span className="p2-number-label" aria-hidden="true" {...scrub.handlers}>{label}</span>
      <TextInput className="p2-number-input" label={label} value={formatNumber(scrub.preview ?? value, bounds.precision)} onCommit={commitText} disabled={disabled} />
      {unit !== undefined && <span className="p2-number-unit">{unit}</span>}
    </label>
  );
}
